import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { LoaderVariant } from '@/components/ui/loader-variants';

/**
 * Internal representation of a loader instance. Keeping the type private to
 * this file ensures we can evolve the loader state structure without breaking
 * consumers of the context.
 */
interface LoaderState {
  id: string;
  message?: string;
  variant?: LoaderVariant;
}

/**
 * Public API surface exposed through the {@link AppLoaderContext}.
 */
interface AppLoaderContextType {
  showLoader: (id: string, message?: string, variant?: LoaderVariant) => void;
  hideLoader: (id: string) => void;
  isLoading: boolean;
  currentMessage?: string;
  currentVariant?: LoaderVariant;
  activeLoaderIds: string[];
}

/**
 * Context handling global application loading indicators. Consumers should use
 * the {@link useAppLoader} hook instead of importing the context directly.
 */
const AppLoaderContext = createContext<AppLoaderContextType | undefined>(undefined);

/**
 * Hook granting access to the global loader management helpers.
 *
 * @throws {Error} When used outside the {@link AppLoaderProvider}. This
 * ensures loader calls are always routed through the provider instance.
 */
export const useAppLoader = () => {
  const context = useContext(AppLoaderContext);
  if (!context) {
    throw new Error('useAppLoader must be used within AppLoaderProvider');
  }
  return context;
};

interface AppLoaderProviderProps {
  children: React.ReactNode;
}

/**
 * Provider maintaining a shared set of loader states across the application.
 *
 * Loader state is stored in a {@link Map} to support concurrent loaders keyed
 * by identifier. The provider exposes helper methods for registering and
 * removing loaders as well as derived state such as the current message.
 */
export const AppLoaderProvider: React.FC<AppLoaderProviderProps> = ({ children }) => {
  const [loaders, setLoaders] = useState<Map<string, LoaderState>>(new Map());
  const loadersRef = useRef(loaders);

  // Keep ref in sync with state
  useEffect(() => {
    loadersRef.current = loaders;
  }, [loaders]);

  const showLoader = useCallback((id: string, message?: string, variant?: LoaderVariant) => {
    setLoaders(prev => {
      const newMap = new Map(prev);
      newMap.set(id, { id, message, variant });
      return newMap;
    });
  }, []);

  const hideLoader = useCallback((id: string) => {
    setLoaders(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  // Clean up any orphaned loaders on unmount
  useEffect(() => {
    return () => {
      loadersRef.current.clear();
    };
  }, []);

  const isLoading = loaders.size > 0;
  // Get the message and variant from the most recently added loader
  const lastLoader = Array.from(loaders.values()).pop();
  const currentMessage = lastLoader?.message;
  const currentVariant = lastLoader?.variant;
  const activeLoaderIds = Array.from(loaders.keys());

  return (
    <AppLoaderContext.Provider value={{ showLoader, hideLoader, isLoading, currentMessage, currentVariant, activeLoaderIds }}>
      {children}
    </AppLoaderContext.Provider>
  );
};

/**
 * Convenience hook for components that need to toggle a loader by identifier.
 */
export const useAppLoading = (loaderId: string, message?: string, variant?: LoaderVariant) => {
  const { showLoader, hideLoader } = useAppLoader();

  /**
   * Automatically removes the loader when the owning component unmounts.
   */
  useEffect(() => {
    return () => {
      // Clean up loader on unmount
      hideLoader(loaderId);
    };
  }, [loaderId, hideLoader]);

  /**
   * Declarative helper for toggling the loader visibility for a component.
   */
  const setLoading = useCallback((loading: boolean, customMessage?: string, customVariant?: LoaderVariant) => {
    if (loading) {
      showLoader(loaderId, customMessage || message, customVariant || variant);
    } else {
      hideLoader(loaderId);
    }
  }, [loaderId, message, variant, showLoader, hideLoader]);

  return setLoading;
};